import { Link } from 'react-router-dom';
import LandingLayout from './LandingLayout';
import {
  FileText,
  Receipt,
  Award,
  Users,
  ShoppingCart,
  Building2,
  TrendingUp,
  CheckCircle,
  ArrowRight,
  Zap,
  Shield,
  Clock,
} from 'lucide-react';

export default function Home() {
  const features = [
    {
      icon: FileText,
      title: 'Invoice Management',
      description: 'Create, send, and track invoices with ease. Automatic payment tracking and overdue reminders.',
    },
    {
      icon: Receipt,
      title: 'Estimates & Quotes',
      description: 'Generate professional estimates that can be converted to invoices with one click.',
    },
    {
      icon: Award,
      title: 'Certification',
      description: 'Manage BS7671 certificates including EIC, EICR, and MEWC documentation.',
    },
    {
      icon: Users,
      title: 'Client Management',
      description: 'Complete CRM for managing your customers, their contact details, and payment history.',
    },
    {
      icon: ShoppingCart,
      title: 'Purchase Tracking',
      description: 'Record supplier purchases and track materials for accurate job costing.',
    },
    {
      icon: Building2,
      title: 'Supplier Management',
      description: 'Keep track of suppliers, credit terms, and purchase history.',
    },
    {
      icon: TrendingUp,
      title: 'Financial Reports',
      description: 'Profit & Loss, Balance Sheet, VAT Summary, and comprehensive financial reporting.',
    },
    {
      icon: Shield,
      title: 'Banking Integration',
      description: 'Multi-account banking with transaction tracking and reconciliation.',
    },
  ];

  const benefits = [
    {
      icon: Zap,
      title: 'Save Time',
      description: 'Automate invoicing, payment tracking, and reporting to focus on your business.',
    },
    {
      icon: Shield,
      title: 'Stay Compliant',
      description: 'UK-specific features including VAT returns and BS7671 certification management.',
    },
    {
      icon: Clock,
      title: 'Access Anywhere',
      description: 'Cloud-based platform accessible from any device, anytime.',
    },
  ];

  return (
    <LandingLayout>
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-orange-50 via-white to-orange-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
          <div className="text-center">
            <img
              src="/sparxmate-logo.png"
              alt="SparxMate"
              className="mx-auto h-16 sm:h-20 w-auto object-contain mb-8"
            />
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6">
              Manage Your Trade Business
              <span className="block text-orange-600 dark:text-orange-500">Like a Pro</span>
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto mb-8">
              The complete business management platform designed specifically for UK tradespeople. 
              Invoicing, estimates, certification, clients, and more—all in one place.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center px-8 py-4 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-semibold text-lg transition-colors shadow-lg hover:shadow-xl"
              >
                Start Free Trial
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <Link
                to="/features"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-semibold text-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Learn More
              </Link>
            </div>
            <p className="mt-6 text-sm text-gray-500 dark:text-gray-400">
              14-day free trial • Sign up without a card • Add payment details in Settings → Subscription before the trial ends • Cancel anytime
            </p>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Everything You Need to Run Your Business
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Powerful features designed specifically for UK tradespeople
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="p-6 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-800 hover:border-orange-500 dark:hover:border-orange-500 transition-colors"
                >
                  <div className="h-12 w-12 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="h-6 w-6 text-orange-600 dark:text-orange-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Why Choose SparxMate?
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="text-center">
                  <div className="inline-flex items-center justify-center h-16 w-16 bg-orange-100 dark:bg-orange-900/30 rounded-full mb-6">
                    <Icon className="h-8 w-8 text-orange-600 dark:text-orange-500" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {benefit.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-orange-600 to-orange-700 dark:from-orange-700 dark:to-orange-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to Transform Your Business?
          </h2>
          <p className="text-xl text-orange-100 mb-8">
            Join thousands of tradespeople who trust SparxMate to manage their business operations.
          </p>
          <Link
            to="/pricing"
            className="inline-flex items-center justify-center px-8 py-4 bg-white text-orange-600 rounded-lg font-semibold text-lg hover:bg-gray-50 transition-colors shadow-lg"
          >
            Start Your Free Trial
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>
    </LandingLayout>
  );
}


